/**
 * The river banks around the walkable district (lifted from lantern-water's
 * `world.js` `townSurroundings`, its banks, rocks, reeds and meadow trees):
 * two terrain sheets from the river's coast outward (`river-course.ts`
 * `riverCoast` and `riverBankHeight`: a muddy shore rising to rolling ground
 * past the rails), drawn with the kit's pixel meadow (`backdrop-ground.ts`);
 * thirty-four rocks with reed clusters along the curved estuary shore south of
 * the town on each side; and eighteen small trees per side outside the
 * authored quarters (whose random draws are still taken, so the rest keep
 * their seed). The prototype's own stream (seed 81027), in its order. Not
 * here: the perimeter rails (`perimeter_rail`), the backdrop quarters and the
 * mooring fingers (their own prefabs). Placed at the origin (the banks follow
 * the town's river). No params.
 */
import * as THREE from 'three';
import { seededRandom } from '../../grid.ts';
import { meadowMaterial } from '../backdrop-ground.ts';
import { riverBankHeight, riverCoast } from '../river-course.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'river_banks',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { mesh, line, cylinder, m, leafMats, plane } = ctx;
    const random = seededRandom(81027);
    const terrain = meadowMaterial(ctx.kit);
    const mud = new THREE.Color();
    const grass = new THREE.Color('#bdc3a0');
    for (const east of [false, true]) {
      const positions: number[] = [];
      const uvs: number[] = [];
      const colors: number[] = [];
      const indices: number[] = [];
      const rows = 96;
      const columns = 30;
      for (let row = 0; row <= rows; row += 1) {
        const z = -112 + row * 2;
        for (let col = 0; col <= columns; col += 1) {
          const t = col / columns;
          const x = THREE.MathUtils.lerp(riverCoast(z, east), east ? 78 : -72, t);
          const y = riverBankHeight(x, z, east);
          positions.push(x, y, z);
          uvs.push(x / 5, z / 5);
          mud.set('#777a70');
          mud.lerp(grass, THREE.MathUtils.smoothstep(Math.abs(x - riverCoast(z, east)), 0.3, 4.5));
          mud.multiplyScalar(0.94 + Math.sin(x * 0.57 + z * 0.18) * Math.sin(z * 0.33) * 0.075);
          colors.push(mud.r, mud.g, mud.b);
          if (row < rows && col < columns) {
            const a = row * (columns + 1) + col;
            const b = a + 1;
            const c = a + columns + 1;
            const d = c + 1;
            if (east) indices.push(a, c, b, b, c, d);
            else indices.push(a, b, c, b, d, c);
          }
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geo.setIndex(indices);
      geo.computeVertexNormals();
      const bank = mesh(root, geo, terrain, 0, 0, 0);
      bank.castShadow = false;
      // Sparse rocks and reed clusters follow the curved estuary shoreline.
      for (let i = 0; i < 34; i += 1) {
        const z = 19 + i * 1.45 + random() * 0.8;
        const coast = riverCoast(z, east);
        const x = coast + (east ? 1 : -1) * (0.3 + random() * 2);
        const ground = riverBankHeight(x, z, east);
        const stone = mesh(root, ctx.sphere, m.stone, x, ground - 0.02, z, 0.28 + random() * 0.65, 0.16 + random() * 0.25, 0.28 + random() * 0.5);
        stone.rotation.y = random() * 6.28;
        for (let j = 0; j < 3; j += 1) {
          const xx = x + (random() - 0.5) * 0.6;
          const zz = z + (random() - 0.5) * 0.6;
          line(root, [xx, ground, zz], [xx + 0.08, ground + 0.5 + random() * 0.45, zz + 0.12], 0.018, m.moss);
        }
      }
      // Small clusters outside the district use their own seed, so scenery cannot reshuffle the town.
      for (let i = 0; i < 18; i += 1) {
        const z = -28 + i * 3.9 + (random() - 0.5) * 2;
        const shoreX = riverCoast(z, east) + (east ? 1 : -1) * (10 + random() * 11);
        const x = east ? Math.max(shoreX, 24 + random() * 12) : Math.min(shoreX, -23 - random() * 12);
        const y = riverBankHeight(x, z, east);
        const size = 0.75 + random() * 0.75;
        if ((!east && x >= -45.2 && x <= -20 && z >= -34 && z <= 24) || (east && x >= 21 && x <= 41.5 && z >= -34 && z <= 33)) {
          // Authored quarters and gardens replace these meadow trees; their leaf draws are still taken.
          for (let j = 0; j < 16; j += 1) random();
          continue;
        }
        cylinder(root, x, y + 1.1 * size, z, 0.12 * size, 2.2 * size, m.darkWood, 0.55);
        for (let j = 0; j < 4; j += 1) {
          const leaf = mesh(root, plane, leafMats[i % 5 === 0 ? 1 : 0], x + (random() - 0.5) * 1.2 * size, y + (2.2 + random() * 0.5) * size, z + (random() - 0.5) * size, 2.4 * size, 2.4 * size, 1);
          leaf.rotation.set(-0.3, (random() - 0.5) * 0.5, 0);
        }
      }
    }
    return { root };
  },
};
